/** Настройки расчёта: отдельный лист поверх карты. Всё из PlannerSettings. */

import { useEffect, useRef, type ReactNode } from 'react';

import type { PlannerSettings } from '@cdf/shared';

import { formatNumber } from '../lib/format';

type NumericKey = {
  [K in keyof PlannerSettings]: PlannerSettings[K] extends number ? K : never;
}[keyof PlannerSettings];

interface SettingsSheetProps {
  open: boolean;
  settings: PlannerSettings;
  defaults: PlannerSettings;
  dirty: boolean;
  onPatch(patch: Partial<PlannerSettings>): void;
  onReset(): void;
  onClose(): void;
  /** Пересчитать маршрут с новыми настройками. */
  onApply(): void;
}

function Section({
  title,
  hint,
  children,
}: {
  title: string;
  hint?: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <section className="settings__section">
      <h3 className="settings__sectionTitle">{title}</h3>
      {hint ? <p className="settings__sectionHint">{hint}</p> : null}
      <div className="settings__rows">{children}</div>
    </section>
  );
}

function Slider({
  id,
  label,
  value,
  min,
  max,
  step,
  unit,
  format,
  onChange,
}: {
  id: string;
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  unit: string;
  format?: (value: number) => string;
  onChange(next: number): void;
}): JSX.Element {
  return (
    <div className="row row--slider">
      <label className="row__label" htmlFor={id}>
        {label}
      </label>
      <output className="row__value num" htmlFor={id}>
        {format ? format(value) : formatNumber(value, step < 1 ? 1 : 0)} {unit}
      </output>
      <input
        id={id}
        className="slider"
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(event) => onChange(Number(event.target.value))}
      />
    </div>
  );
}

function NumberRow({
  id,
  label,
  value,
  unit,
  step = 1,
  min = 0,
  onChange,
}: {
  id: string;
  label: string;
  value: number;
  unit: string;
  step?: number;
  min?: number;
  onChange(next: number): void;
}): JSX.Element {
  return (
    <div className="row row--number">
      <label className="row__label" htmlFor={id}>
        {label}
      </label>
      <div className="row__control">
        <input
          id={id}
          className="field__input field__input--num num"
          type="number"
          inputMode="numeric"
          min={min}
          step={step}
          value={value}
          onChange={(event) => {
            const next = Number(event.target.value);
            if (Number.isFinite(next)) onChange(next);
          }}
        />
        <span className="row__unit">{unit}</span>
      </div>
    </div>
  );
}

function Segmented<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T;
  options: Array<{ value: T; label: string; hint?: string }>;
  onChange(next: T): void;
}): JSX.Element {
  return (
    <div className="row row--segmented">
      <span className="row__label" id={`${label}-label`}>
        {label}
      </span>
      <div className="segmented" role="radiogroup" aria-labelledby={`${label}-label`}>
        {options.map((option) => (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={value === option.value}
            className={`segmented__item${value === option.value ? ' is-on' : ''}`}
            onClick={() => onChange(option.value)}
          >
            <span className="segmented__label">{option.label}</span>
            {option.hint ? (
              <span className="segmented__hint">{option.hint}</span>
            ) : null}
          </button>
        ))}
      </div>
    </div>
  );
}

export function SettingsSheet({
  open,
  settings,
  defaults,
  dirty,
  onPatch,
  onReset,
  onClose,
  onApply,
}: SettingsSheetProps): JSX.Element | null {
  const closeRef = useRef<HTMLButtonElement>(null);

  // Escape закрывает лист — на планшете с клавиатурой это ожидаемо.
  useEffect(() => {
    if (!open) return;
    closeRef.current?.focus();
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  // Ключ вычисляемый, поэтому тип сужаем руками — NumericKey гарантирует число.
  const set =
    (key: NumericKey) =>
    (next: number): void =>
      onPatch({ [key]: next } as Partial<PlannerSettings>);

  return (
    <div className="modal" role="dialog" aria-modal="true" aria-label="Настройки расчёта">
      <button
        type="button"
        className="modal__scrim"
        aria-label="Закрыть настройки"
        onClick={onClose}
      />
      <div className="modal__panel">
        <header className="modal__head">
          <h2 className="modal__title">Настройки расчёта</h2>
          <button
            type="button"
            className="iconbtn"
            ref={closeRef}
            onClick={onClose}
            aria-label="Закрыть"
          >
            ✕
          </button>
        </header>

        <div className="modal__body">
          <Section title="Цель" hint="1 бонус = 1 рубль">
            <NumberRow
              id="targetBonus"
              label="Хочу заработать"
              unit="₽"
              step={1000}
              value={settings.targetBonus}
              onChange={set('targetBonus')}
            />
            <NumberRow
              id="bonusPerRefuel"
              label="Бонус за заправку"
              unit="₽"
              step={100}
              value={settings.bonusPerRefuel}
              onChange={set('bonusPerRefuel')}
            />
          </Section>

          <Section
            title="Топливо"
            hint="Датчик врёт, поэтому к минимуму добавляем запас, а часть бака считаем неизвлекаемой."
          >
            <Slider
              id="minLiters"
              label="Минимум литров за бонус"
              unit="л"
              min={5}
              max={40}
              step={1}
              value={settings.minLitersForBonus}
              onChange={set('minLitersForBonus')}
            />
            <Slider
              id="safety"
              label="Запас сверху"
              unit="л"
              min={0}
              max={10}
              step={0.5}
              value={settings.litersSafetyMargin}
              onChange={set('litersSafetyMargin')}
            />
            <Slider
              id="reserve"
              label="Неизвлекаемый остаток"
              unit="л"
              min={0}
              max={15}
              step={0.5}
              value={settings.tankReserveLiters}
              onChange={set('tankReserveLiters')}
            />
          </Section>

          <Section title="Поиск">
            <Slider
              id="radius"
              label="Радиус поиска"
              unit="км"
              min={1}
              max={50}
              step={1}
              value={settings.searchRadiusKm}
              onChange={set('searchRadiusKm')}
            />
            <Slider
              id="maxStops"
              label="Максимум машин"
              unit="шт"
              min={1}
              max={60}
              step={1}
              value={settings.maxStops}
              onChange={set('maxStops')}
            />
            <Slider
              id="budget"
              label="Бюджет времени"
              unit=""
              min={30}
              max={16 * 60}
              step={30}
              value={settings.maxDurationMin}
              format={(minutes) =>
                minutes >= 60
                  ? `${Math.floor(minutes / 60)} ч${minutes % 60 ? ` ${minutes % 60} мин` : ''}`
                  : `${minutes} мин`
              }
              onChange={set('maxDurationMin')}
            />
          </Section>

          <Section title="Как перемещаемся">
            <Segmented
              label="До первой машины"
              value={settings.firstLegMode}
              options={[
                { value: 'pedestrian', label: 'Пешком' },
                { value: 'taxi', label: 'Такси' },
                { value: 'driving', label: 'На своей' },
              ]}
              onChange={(next) => onPatch({ firstLegMode: next })}
            />
            <Segmented
              label="Между машинами"
              value={settings.chainMode}
              options={[
                {
                  value: 'drive',
                  label: 'Доезжаю',
                  hint: 'на заправленной машине',
                },
                { value: 'walk', label: 'Пешком', hint: 'бросаю на АЗС' },
              ]}
              onChange={(next) => onPatch({ chainMode: next })}
            />
            <Slider
              id="maxWalk"
              label="Дальше пешком не иду"
              unit="км"
              min={0.2}
              max={5}
              step={0.1}
              value={settings.maxWalkKm}
              onChange={set('maxWalkKm')}
            />
          </Section>

          <Section title="Время и скорости">
            <Slider
              id="refuelMinutes"
              label="Минут на заправку"
              unit="мин"
              min={2}
              max={25}
              step={1}
              value={settings.refuelMinutes}
              onChange={set('refuelMinutes')}
            />
            <Slider
              id="approachMinutes"
              label="Подход к машине"
              unit="мин"
              min={0}
              max={15}
              step={1}
              value={settings.approachMinutes}
              onChange={set('approachMinutes')}
            />
            <Slider
              id="speedDriving"
              label="Скорость по городу"
              unit="км/ч"
              min={10}
              max={70}
              step={1}
              value={settings.speedDrivingKmh}
              onChange={set('speedDrivingKmh')}
            />
            <Slider
              id="speedWalking"
              label="Скорость пешком"
              unit="км/ч"
              min={2}
              max={8}
              step={0.5}
              value={settings.speedWalkingKmh}
              onChange={set('speedWalkingKmh')}
            />
          </Section>

          <Section title="Расходы" hint="Ставьте 0, если заправочные поездки бесплатные.">
            <NumberRow
              id="rental"
              label="Минута аренды"
              unit="₽"
              value={settings.rentalCostPerMinute}
              onChange={set('rentalCostPerMinute')}
            />
            <NumberRow
              id="taxi"
              label="Такси за км"
              unit="₽"
              value={settings.taxiCostPerKm}
              onChange={set('taxiCostPerKm')}
            />
          </Section>

          <p className="settings__note">
            Значения по умолчанию приходят с сервера: цель{' '}
            {formatNumber(defaults.targetBonus)} ₽, радиус {defaults.searchRadiusKm} км.
          </p>
        </div>

        <footer className="modal__foot">
          <button
            type="button"
            className="btn btn--ghost"
            onClick={onReset}
            disabled={!dirty}
          >
            Сбросить
          </button>
          <button
            type="button"
            className="btn btn--primary btn--grow"
            onClick={() => {
              onApply();
              onClose();
            }}
          >
            Пересчитать маршрут
          </button>
        </footer>
      </div>
    </div>
  );
}
